import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { fetchApi } from '../../../../redux/fetch/fetch.actions';
import { Dispatch } from 'redux';
import { GlobalState } from '../../../../redux/root-reducer';
import { useRouteMatch, useHistory } from 'react-router-dom';
import Loading from '../../../../components/loading/loading.component';
import QuestionActionAPI, {
    IFetchQuestionPayload,
    IUpdateQuestionPayload,
} from '../../../../redux/question/question.actions';
import { Question } from '../../../../@types/question.interface';
import UpdateQuestionForm from '../../../../components/update-question/update-question.component';
import './update-question-page.styles.scss';
import PreviewQuestion from '../../../../components/preview-question/preview-question.component';
import { validateInsertQuestion } from '../../../../utils/validation';
import useAfterFetch from '../../../../effects/useAfterFetch.effect';

interface IProps {
    isFetching?: boolean;
    errors?: string | null;
    question: Question | null;
    fetchQuestion: (
        param: IFetchQuestionPayload['param'],
        data: IFetchQuestionPayload['data']
    ) => void;
    updateQuestion: (data: IUpdateQuestionPayload['data']) => void;
    clearQuestions: () => void;
    clearQuestion: () => void;
}
function UpdateQuestionContainer({
    isFetching,
    errors,
    question,
    fetchQuestion,
    updateQuestion,
    clearQuestions,
    clearQuestion,
}: IProps) {
    const match = useRouteMatch<{ id: string; number: string }>();
    const history = useHistory();
    const id = parseInt(match.params.id);
    const number = parseInt(match.params.number);
    const fetching = useRef(false);
    const param = {
        quiz_id: id,
    };

    const data = {
        question_number: number,
    };

    const [tempQuestion, setTempQuestion] = useState<Question>({
        quiz_id: id,
        question_type_id: 0,
        question_id: 0,
        question_number: number,
        question_quiz: '',
        weight_score: 0,
        reason_answer: "",
        question_answer: [{ key: '' }],
        question_choice: [
            { answer: '' },
            { answer: '' },
            { answer: '' },
            { answer: '' },
        ],
    });

    useEffect(() => {
        if (isFetching || errors) return;
        if (question === null) fetchQuestion(param, data);
    }, [isFetching, errors, param, data, question, fetchQuestion]);

    useEffect(() => {
        if (question) {
            setTempQuestion((prevState) => ({
                ...prevState,
                question_id: question.question_id,
                question_type_id: question.question_type_id,
                question_quiz: question.question_quiz,
                reason_answer: question.reason_answer,
                weight_score: question.weight_score,
                question_choice: question.question_choice,
            }));
        }
    }, [question]);

    useEffect(() => {
        if (isFetching === false && fetching.current === true)
            history.push(`/quiz/${id}/question`);
        return () => {
            clearQuestions();
            clearQuestion();
        };
    }, [isFetching, id, history, clearQuestions, clearQuestion]);

    const handleChange = (evt: any, name: string, index?: number) => {
        let val = evt;

        if (name !== 'question_quiz' && name !== 'question_choice' && name !== 'reason_answer')
            val = evt.target.value;
        if (name === 'question_type_id' || name === 'weight_score')
            val = parseInt(val);
        if (val < 0) {
            val = 0;
            evt.target.value = 0;
        }
        let questionTemp = Object.assign({}, tempQuestion);

        if (name === 'question_quiz') {
            questionTemp.question_quiz = val;
            setTempQuestion((prevState) => ({
                ...prevState,
                question_quiz: questionTemp.question_quiz,
            }));
        } else if (name === 'question_answer') {
            if (index === undefined) index = 0;
            if (questionTemp.question_type_id === 1) {
                questionTemp.question_answer[index].key = val;
            } else if (questionTemp.question_type_id === 2) {
                if (evt.target.checked) {
                    if (
                        questionTemp.question_answer.length === 1 &&
                        questionTemp.question_answer[0].key === ''
                    )
                        questionTemp.question_answer[0].key = val;
                    else
                        questionTemp.question_answer = questionTemp.question_answer.concat(
                            { key: val }
                        );
                } else {
                    let n = -2;
                    for (
                        let i = 0;
                        i < questionTemp.question_answer.length;
                        i++
                    )
                        if (questionTemp.question_answer[i].key === val) n = i;
                    questionTemp.question_answer.splice(n, 1);
                }
            }
            setTempQuestion((prevState) => ({
                ...prevState,
                question_answer: questionTemp.question_answer,
            }));
        } else if (name === 'question_choice') {
            if (index === undefined) index = 0;
            questionTemp.question_choice[index].answer = val;
            setTempQuestion((prevState) => ({
                ...prevState,
                question_choice: questionTemp.question_choice,
            }));
        } else {
            setTempQuestion((prevState) => ({
                ...prevState,
                [name]: val,
            }));
        }
    };

    const [, hasSubmitted] = useAfterFetch(
        isFetching,
        undefined,
        clearQuestions
    );
    const [error, setError] = useState('');
    const handleClick = async (evt: any) => {
        evt.preventDefault();
        setError(validateInsertQuestion(tempQuestion, errors));
        if (validateInsertQuestion(tempQuestion, errors) === '') {
            updateQuestion(tempQuestion);
            hasSubmitted.current = true;
            fetching.current = true;
        }
    };
    return (
        <div className="container">
            {isFetching ? (
                <Loading className="fixed" />
            ) : (
                <div className="container__content">
                    <div className="update-question">
                        <div className="update-question__title">
                            <h2 className="heading">Update Question</h2>
                        </div>
                        <div className="update-question__detail">
                            <PreviewQuestion question={tempQuestion} />
                            <UpdateQuestionForm
                                error={error}
                                handleChange={handleChange}
                                handleClick={handleClick}
                                question={tempQuestion}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = ({
    fetchController: { isFetching, errors },
    question: { question },
}: GlobalState) => ({
    isFetching: isFetching.UPDATE_QUESTION,
    errors: errors.UPDATE_QUESTION,
    question,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchQuestion: (
        param: IFetchQuestionPayload['param'],
        data: IFetchQuestionPayload['data']
    ) => dispatch(fetchApi({ name: 'QUESTION', param: param, data: data })),
    updateQuestion: (data: IUpdateQuestionPayload['data']) =>
        dispatch(fetchApi({ name: 'UPDATE_QUESTION', data })),
    clearQuestions: () => dispatch(QuestionActionAPI.clearQuestions()),
    clearQuestion: () => dispatch(QuestionActionAPI.clearQuestion()),
});

const UpdateQuestionPage = connect(
    mapStateToProps,
    mapDispatchToProps
)(UpdateQuestionContainer);

export default UpdateQuestionPage;
